import {Injectable, signal} from '@angular/core';
import {STYLE_GROUP_EXPAND_STORAGE_KEY} from '../storage-keys';

/**
 * MAP-990: tracks which "core style" groups (color, shape, label font size, ...)
 * are expanded to reveal their dependent styles, in the node/link/smap style
 * sections.
 *
 * - User-level preference, not per-map: a single localStorage blob keyed by a
 *   stable group id (e.g. 'node.color', 'link.labelFontSize', 'smap.appearance'),
 *   so switching maps never resets it (AC12) and it survives refresh/new
 *   sessions (AC13) — the same plain-localStorage idiom already used by
 *   MaporiumAccordionItemComponent and app-more-less-btn for similar
 *   user-level UI prefs (no per-user backend settings table exists for this).
 * - Collapsed by default (AC11): an id with no stored entry is treated as false.
 * - Cross-tab sync (AC14): no BroadcastChannel/websocket settings-push exists
 *   anywhere in this codebase yet, so this listens for the `storage` event
 *   (fired in OTHER tabs when localStorage changes) as the simplest correct
 *   fallback the ticket allows.
 */
@Injectable({providedIn: 'root'})
export class StyleGroupExpansionService {
  private readonly state = signal<Record<string, boolean>>(this.readStorage());

  constructor() {
    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key === STYLE_GROUP_EXPAND_STORAGE_KEY) {
        this.state.set(this.readStorage());
      }
    });
  }

  isExpanded(groupId: string): boolean {
    return this.state()[groupId] === true;
  }

  toggle(groupId: string): void {
    const next = {...this.state(), [groupId]: !this.isExpanded(groupId)};
    this.state.set(next);
    this.writeStorage(next);
  }

  private readStorage(): Record<string, boolean> {
    try {
      const raw = localStorage.getItem(STYLE_GROUP_EXPAND_STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }

  private writeStorage(value: Record<string, boolean>): void {
    try {
      localStorage.setItem(STYLE_GROUP_EXPAND_STORAGE_KEY, JSON.stringify(value));
    } catch {
      // Ignore quota/availability errors — expansion state is a nice-to-have.
    }
  }
}
